import type { ConfigurationContextValue } from "./ConfigurationContext";
import type {
  CampaignDef,
  ContactListDef,
  DashboardDef,
  CustomFieldDef,
  EmailTemplate,
  IntegrationConfig,
  ScoringRule,
  SequenceDef,
  SidebarModulePref,
  SlateForm,
  WorkflowDef,
} from "../types";
// Import the logos as module assets so Vite resolves their URL relative to the
// JS chunk (import.meta.url), not the current route. A plain "./logos/..." path
// breaks on nested routes like /oauth/consent and under a deployment sub-path.
import darkModeLogo from "./logos/logo_obvious_dark.svg";
import lightModeLogo from "./logos/logo_obvious_light.svg";

export const defaultDarkModeLogo = darkModeLogo;
export const defaultLightModeLogo = lightModeLogo;

export const defaultCurrency = "USD";

export const defaultTitle = "Obvious";

export const defaultCompanySectors = [
  { value: "communication-services", label: "Communication Services" },
  { value: "consumer-discretionary", label: "Consumer Discretionary" },
  { value: "consumer-staples", label: "Consumer Staples" },
  { value: "energy", label: "Energy" },
  { value: "financials", label: "Financials" },
  { value: "health-care", label: "Health Care" },
  { value: "industrials", label: "Industrials" },
  { value: "information-technology", label: "Information Technology" },
  { value: "materials", label: "Materials" },
  { value: "real-estate", label: "Real Estate" },
  { value: "utilities", label: "Utilities" },
];

export const defaultDealStages = [
  { value: "opportunity", label: "Opportunity", winProbability: 20 },
  { value: "proposal-sent", label: "Proposal Sent", winProbability: 40 },
  { value: "in-negociation", label: "In Negotiation", winProbability: 60 },
  { value: "won", label: "Won", winProbability: 100 },
  { value: "lost", label: "Lost", winProbability: 0 },
  { value: "delayed", label: "Delayed", winProbability: 10 },
];

export const defaultDealPipelineStatuses = ["won"];

export const defaultDealCategories = [
  { value: "other", label: "Other" },
  { value: "copywriting", label: "Copywriting" },
  { value: "print-project", label: "Print project" },
  { value: "ui-design", label: "UI Design" },
  { value: "website-design", label: "Website design" },
];

export const defaultCustomFields: CustomFieldDef[] = [];

/**
 * Starter marketing assets so the demo has something to drill into - fills,
 * enrollments, and sends simulate deterministically (see assetActivity.ts)
 * and are labeled sample data. Delete or edit them like any asset.
 */
export const defaultForms: SlateForm[] = [
  {
    id: "form-contact",
    name: "Contact us",
    submitLabel: "Send message",
    successMessage: "Thanks - we'll be in touch shortly.",
    fields: [
      { id: "fc-1", source: "first_name", label: "First name", type: "text", required: true },
      { id: "fc-2", source: "last_name", label: "Last name", type: "text", required: false },
      { id: "fc-3", source: "email", label: "Work email", type: "email", required: true },
      { id: "fc-4", source: "company", label: "Company", type: "text", required: false },
      { id: "fc-5", source: "message", label: "How can we help?", type: "textarea", required: false },
    ],
  },
  {
    id: "form-newsletter",
    name: "Newsletter signup",
    submitLabel: "Subscribe",
    successMessage: "You're on the list.",
    fields: [
      { id: "fn-1", source: "email", label: "Email", type: "email", required: true },
      { id: "fn-2", source: "consent", label: "Send me the monthly growth notes", type: "checkbox", required: true },
    ],
  },
];

export const defaultEmailTemplates: EmailTemplate[] = [
  {
    id: "tpl-nurture",
    name: "Intro + case study",
    subject: "How teams like yours grow pipeline",
    preheader: "One idea, one case study, three minutes.",
    accentColor: "#2e6ba9",
    blocks: [
      { id: "tn-1", type: "logo" },
      { id: "tn-2", type: "heading", text: "One idea worth stealing" },
      {
        id: "tn-3",
        type: "text",
        text: "Here's how a team about your size turned form fills into booked calls in under two weeks - and the exact play they ran.",
      },
      { id: "tn-4", type: "button", text: "Read the case study", url: "https://gogreymatter.com/case-study" },
    ],
  },
];

export const defaultMailingAddress = "";

export const defaultMarketingFromEmail = "";

export const defaultMarketingFromName = "";

export const defaultMarketingTrackEngagement = true;

export const defaultMarketingApprovedDomain = "";

export const defaultTurnstileSiteKey = "";

export const defaultContactLists: ContactListDef[] = [
  { id: "list-all-contacts", name: "All contacts", logic: "all", filters: [] },
];

export const defaultSequences: SequenceDef[] = [
  {
    id: "seq-followup",
    name: "New lead follow-up",
    listId: "list-all-contacts",
    unenrollOnReply: true,
    businessHoursOnly: true,
    skipIfInAnySequence: true,
    steps: [
      { id: "sf-1", type: "email", templateId: "tpl-nurture", senderMode: "owner" },
      { id: "sf-2", type: "wait", days: 3 },
      {
        id: "sf-3",
        type: "task",
        taskTitle: "Call {{first_name}} - reference the last email",
        assignee: { mode: "owner" },
        taskType: "call",
      },
      { id: "sf-4", type: "wait", days: 4 },
      {
        id: "sf-5",
        type: "email",
        templateId: "tpl-nurture",
        senderMode: "owner",
        threadWithPrevious: true,
      },
    ],
  },
];

/**
 * The seeded campaign carries fabricated send history ("sent" + a date), so
 * it exists ONLY in demo builds. Live deployments start with no campaigns -
 * the starter forms/sequence/workflows stay everywhere because their
 * activity counters honestly read zero until the engines run.
 */
export const defaultCampaigns: CampaignDef[] =
  import.meta.env.VITE_IS_DEMO === "true"
    ? [
        {
          id: "cmp-launch",
          name: "August newsletter",
          templateId: "tpl-nurture",
          listId: "list-all-contacts",
          status: "sent",
          sentAt: "2026-08-10T14:30:00.000Z",
        },
      ]
    : [];

export const defaultDashboards: DashboardDef[] = [];

export const defaultScoringRules: ScoringRule[] = [
  { key: "email_engagement", label: "Engages with your emails", points: 10 },
  { key: "site_return", label: "Keeps coming back to the site (3+ touches)", points: 8 },
  { key: "inbound", label: "Came in through a form, search, or campaign", points: 10 },
  { key: "recent_activity", label: "Active in the last 7 days", points: 15 },
  { key: "active_month", label: "Active in the last 30 days", points: 5 },
  { key: "status_hot", label: "Marked hot", points: 20 },
  { key: "status_warm", label: "Marked warm", points: 10 },
  { key: "seniority", label: "Senior title (VP, Director, C-level, Founder)", points: 12 },
  { key: "newsletter", label: "Subscribed to the newsletter", points: 5 },
  { key: "open_deal", label: "On an open deal", points: 15 },
];

export const defaultWorkflows: WorkflowDef[] = [
  {
    id: "wf-form-score",
    name: "Form submitted → route the lead",
    enabled: false,
    trigger: { type: "form_submitted" },
    actions: [
      { id: "wfs-1", type: "adjust_score", params: { value: "10" } },
      { id: "wfs-2", type: "add_tag", params: { value: "inbound" } },
      {
        id: "wfs-3",
        type: "set_owner",
        config: { assignee: { mode: "round_robin", poolIds: [] }, onlyIfUnowned: true },
      },
      {
        id: "wfs-b1",
        type: "branch",
        condition: { kind: "score_gte", value: "50" },
        then: [
          {
            id: "wfs-4",
            type: "create_task",
            params: { value: "Hot inbound: call {{first_name}} {{last_name}} today" },
            config: { assignee: { mode: "owner" }, dueInDays: 0, taskType: "Call" },
          },
          {
            id: "wfs-5",
            type: "notify_user",
            config: {
              assignee: { mode: "owner" },
              message: "{{first_name}} {{last_name}} submitted a form and is already hot",
            },
          },
        ],
        otherwise: [
          {
            id: "wfs-6",
            type: "enroll_sequence",
            params: { value: "seq-followup" },
            config: { senderMode: "owner", skipIfEnrolled: true },
          },
        ],
      },
    ],
  },
  {
    id: "wf-quiet",
    name: "Gone quiet → follow-up task",
    enabled: false,
    trigger: { type: "no_activity", params: { value: "30" } },
    actions: [
      {
        id: "wfq-1",
        type: "create_task",
        params: { value: "Re-engage {{first_name}} - no activity in 30 days" },
        config: { assignee: { mode: "owner" }, dueInDays: 1, taskType: "call" },
      },
      { id: "wfq-2", type: "adjust_score", params: { value: "-10" } },
    ],
  },
  // ----- Starter library (round 44): small, ready to arm, off by default -----
  {
    id: "wf-new-contact-intro",
    name: "New contact → intro task after 3 days",
    enabled: false,
    trigger: { type: "contact_created" },
    actions: [
      {
        id: "wfn-1",
        type: "set_owner",
        config: { assignee: { mode: "round_robin", poolIds: [] }, onlyIfUnowned: true },
      },
      { id: "wfn-2", type: "delay", days: 3 },
      {
        id: "wfn-3",
        type: "create_task",
        params: { value: "Say hello to {{first_name}} at {{company}}" },
        config: { assignee: { mode: "owner" }, dueInDays: 0, taskType: "email" },
      },
    ],
  },
  {
    id: "wf-hot-alert",
    name: "Score crosses 65 → owner alert + same-day call",
    enabled: false,
    trigger: { type: "score_crossed", params: { threshold: "65" } },
    actions: [
      {
        id: "wfh-1",
        type: "notify_user",
        config: {
          assignee: { mode: "owner" },
          message: "{{first_name}} {{last_name}} just crossed 65 - strike while it's warm",
        },
      },
      {
        id: "wfh-2",
        type: "create_task",
        params: { value: "Call {{first_name}} today - hot lead" },
        config: { assignee: { mode: "owner" }, dueInDays: 0, taskType: "call" },
      },
    ],
  },
  {
    id: "wf-deal-kickoff",
    name: "Deal created → kickoff checklist",
    enabled: false,
    trigger: { type: "deal_created" },
    actions: [
      {
        id: "wfd-1",
        type: "create_task",
        params: { value: "Kickoff for {{company}}: confirm scope, timeline, and who signs" },
        config: { assignee: { mode: "owner" }, dueInDays: 1, taskType: "meeting" },
      },
      { id: "wfd-2", type: "add_tag", params: { value: "in-pipeline" } },
    ],
  },
  {
    id: "wf-reply-momentum",
    name: "They replied → keep the thread alive",
    enabled: false,
    trigger: { type: "email_replied" },
    actions: [
      { id: "wfr-1", type: "adjust_score", params: { value: "10" } },
      {
        id: "wfr-2",
        type: "create_task",
        params: { value: "{{first_name}} replied - answer within the day" },
        config: { assignee: { mode: "owner" }, dueInDays: 0, taskType: "email" },
      },
    ],
  },
  {
    id: "wf-hot-tag",
    name: "Tagged hot-lead → route and chase",
    enabled: false,
    trigger: { type: "tag_added", params: { value: "hot-lead" } },
    actions: [
      {
        id: "wft-1",
        type: "set_owner",
        config: { assignee: { mode: "round_robin", poolIds: [] }, onlyIfUnowned: true },
      },
      { id: "wft-2", type: "adjust_score", params: { value: "15" } },
      {
        id: "wft-3",
        type: "create_task",
        params: { value: "Hot lead: call {{first_name}} {{last_name}}" },
        config: { assignee: { mode: "owner" }, dueInDays: 0, taskType: "call" },
      },
    ],
  },
  {
    id: "wf-nurture-unowned",
    name: "Unowned after a week → nurture sequence",
    enabled: false,
    trigger: { type: "contact_created" },
    actions: [
      { id: "wfu-1", type: "delay", days: 7 },
      {
        id: "wfu-2",
        type: "branch",
        condition: { kind: "unowned" },
        then: [
          {
            id: "wfu-3",
            type: "enroll_sequence",
            params: { value: "seq-followup" },
            config: { senderMode: "owner", skipIfEnrolled: true },
          },
        ],
        otherwise: [],
      },
    ],
  },
  {
    id: "wf-stage-stalled",
    name: "Deal moved → nudge the owner",
    enabled: false,
    trigger: { type: "deal_stage_changed" },
    actions: [
      {
        id: "wfs-stage-1",
        type: "notify_user",
        config: {
          assignee: { mode: "owner" },
          message: "A deal with {{company}} changed stage - check the next step is booked",
        },
      },
    ],
  },
  {
    id: "wf-quiet-60",
    name: "Quiet 60 days → last-touch then archive tag",
    enabled: false,
    trigger: { type: "no_activity", params: { value: "60" } },
    actions: [
      {
        id: "wf60-1",
        type: "create_task",
        params: { value: "Last touch for {{first_name}} - 60 days silent" },
        config: { assignee: { mode: "owner" }, dueInDays: 1, taskType: "email" },
      },
      { id: "wf60-2", type: "delay", days: 7 },
      { id: "wf60-3", type: "add_tag", params: { value: "dormant" } },
      { id: "wf60-4", type: "adjust_score", params: { value: "-20" } },
    ],
  },
];

/** Defaults for the dashboard's "Keep an eye on" panel - see Settings → Keep an eye on. */
export const defaultSignals = {
  heatingUp: { enabled: true, minScore: 45, activeDays: 14 },
  slipping: { enabled: true, quietDays: 21, minAmount: 0 },
  overdue: { enabled: true, graceDays: 0 },
  perColumn: 3,
};

export const defaultCompanySidebarModules: SidebarModulePref[] = [
  { key: "info" },
  { key: "tags" },
  { key: "hierarchy" },
  { key: "context" },
  { key: "address" },
  { key: "additional" },
  { key: "custom" },
];

export const defaultIntegrations: Record<string, IntegrationConfig> = {};

export const defaultNoteStatuses = [
  { value: "cold", label: "Cold", color: "#9db4cc" },
  { value: "warm", label: "Warm", color: "#d9b36b" },
  { value: "hot", label: "Hot", color: "#c97a63" },
  { value: "in-contract", label: "In Contract", color: "#8fb586" },
];

export const defaultTimezone = "America/New_York";

export const defaultCompanyProfile = {
  companyName: "Grey Matter (demo workspace)",
  websiteUrl: "https://gogreymatter.com",
  industry: "Professional services",
  phone: "",
};

/** Seeded at provisioning; managed from the operator console (decision #17). */
export const defaultPlanLimits = { maxUsers: 5, maxContacts: 1000 };

export const defaultLifecycleStages = [
  { value: "subscriber", label: "Subscriber" },
  { value: "lead", label: "Lead" },
  { value: "mql", label: "MQL" },
  { value: "sql", label: "SQL" },
  { value: "opportunity", label: "Opportunity" },
  { value: "customer", label: "Customer" },
];

export const defaultCloseReasons = [
  { value: "price", label: "Price" },
  { value: "timing", label: "Timing" },
  { value: "competitor", label: "Went with a competitor" },
  { value: "no_decision", label: "No decision" },
  { value: "other", label: "Other" },
];

export const defaultTaskTypes = [
  { value: "none", label: "None" },
  { value: "email", label: "Email" },
  { value: "demo", label: "Demo" },
  { value: "lunch", label: "Lunch" },
  { value: "meeting", label: "Meeting" },
  { value: "follow-up", label: "Follow-up" },
  { value: "thank-you", label: "Thank you" },
  { value: "ship", label: "Ship" },
  { value: "call", label: "Call" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "quote", label: "Quote" },
];

export const defaultConfiguration: ConfigurationContextValue = {
  companySectors: defaultCompanySectors,
  currency: defaultCurrency,
  dealCategories: defaultDealCategories,
  dealPipelineStatuses: defaultDealPipelineStatuses,
  dealStages: defaultDealStages,
  customFields: defaultCustomFields,
  forms: defaultForms,
  emailTemplates: defaultEmailTemplates,
  mailingAddress: defaultMailingAddress,
  marketingFromEmail: defaultMarketingFromEmail,
  marketingFromName: defaultMarketingFromName,
  marketingTrackEngagement: defaultMarketingTrackEngagement,
  marketingApprovedDomain: defaultMarketingApprovedDomain,
  turnstileSiteKey: defaultTurnstileSiteKey,
  contactLists: defaultContactLists,
  sequences: defaultSequences,
  campaigns: defaultCampaigns,
  dashboards: defaultDashboards,
  companySidebarModules: defaultCompanySidebarModules,
  scoringRules: defaultScoringRules,
  workflows: defaultWorkflows,
  signals: defaultSignals,
  integrations: defaultIntegrations,
  noteStatuses: defaultNoteStatuses,
  timezone: defaultTimezone,
  companyProfile: defaultCompanyProfile,
  planLimits: defaultPlanLimits,
  lifecycleStages: defaultLifecycleStages,
  closeReasons: defaultCloseReasons,
  taskTypes: defaultTaskTypes,
  title: defaultTitle,
  darkModeLogo: defaultDarkModeLogo,
  lightModeLogo: defaultLightModeLogo,
};
